import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Layers,
  AppWindow,
  GitBranch,
  Monitor,
  Network,
  AlertTriangle,
  Package,
  Building2,
  ShieldCheck,
  Compass,
  CheckCircle2,
} from 'lucide-react';

interface TiersLogicielsAgentiquesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'synthese' | 'tiers' | 'produits' | 'cas' | 'gouvernance' | 'limites';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'synthese', label: 'Synthèse', icon: Compass },
  { id: 'tiers', label: 'Tiers 1-4', icon: Layers },
  { id: 'produits', label: 'Produits', icon: Package },
  { id: 'cas', label: 'Cas d’usage', icon: Building2 },
  { id: 'gouvernance', label: 'Lecture PRIsME', icon: ShieldCheck },
  { id: 'limites', label: 'Limites', icon: AlertTriangle },
];

function SectionCard({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-lg">
      <h4 className="mb-4 flex items-center gap-2 text-lg font-black text-white">
        <Icon className="h-5 w-5 text-cyan-300" />
        {title}
      </h4>
      {children}
    </div>
  );
}

function TierCard({
  tier,
  title,
  icon: Icon,
  color,
  definition,
  examples,
  boundary,
}: {
  tier: string;
  title: string;
  icon: React.ElementType;
  color: string;
  definition: string;
  examples: string[];
  boundary: string;
}) {
  return (
    <div className={`rounded-2xl border ${color} bg-white/5 p-5`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-white/10 p-2">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-200">{tier}</p>
            <h5 className="text-lg font-black text-white">{title}</h5>
          </div>
        </div>
      </div>
      <p className="mb-4 text-sm leading-relaxed text-slate-300">{definition}</p>
      <div className="mb-4 space-y-2">
        {examples.map((example) => (
          <div key={example} className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-200">
            {example}
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-amber-300/20 bg-amber-400/10 p-3 text-xs leading-relaxed text-amber-100">
        <strong>Frontière :</strong> {boundary}
      </div>
    </div>
  );
}

function SimpleTable({ headers, rows }: { headers: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-white/10">
          <tr>
            {headers.map((header) => (
              <th key={header} className="px-4 py-3 text-left font-bold text-cyan-200">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-t border-white/10 hover:bg-white/5">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-4 py-3 align-top text-slate-300">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SyntheseTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Idée centrale" icon={Compass}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-cyan-300/20 bg-cyan-400/10 p-5">
            <p className="mb-2 text-sm font-bold uppercase tracking-[0.2em] text-cyan-200">Définition PRIsME proposée</p>
            <p className="text-sm leading-relaxed text-slate-200">
              Les tiers logiciels agentiques classent les agents selon leur <strong className="text-white">portée d’action</strong> :
              une app, un workflow, un poste de travail ou une couche d’orchestration agentique.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-300/20 bg-amber-400/10 p-5">
            <p className="mb-2 text-sm font-bold uppercase tracking-[0.2em] text-amber-200">Statut normatif</p>
            <p className="text-sm leading-relaxed text-slate-200">
              Ce n’est <strong className="text-white">pas encore une norme officielle</strong> reconnue comme ISO, NIST ou OWASP.
              C’est une grille de marché et d’architecture pour aider BZ à raisonner clairement.
            </p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Image mentale simple" icon={Layers}>
        <div className="grid gap-3 md:grid-cols-4">
          {[
            ['Tier 1', 'Dans une app', 'Assistant spécialisé dans un contexte fermé.'],
            ['Tier 2', 'Entre les apps', 'Agent qui suit un processus métier balisé.'],
            ['Tier 3', 'Sur le poste', 'Agent qui utilise l’ordinateur comme un humain.'],
            ['Tier 4', 'Au-dessus du système', 'Agent OS / runtime qui orchestre agents, outils, mémoire et permissions.'],
          ].map(([tier, title, desc]) => (
            <div key={tier} className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-200">{tier}</p>
              <p className="mt-1 text-base font-black text-white">{title}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-400">{desc}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Pourquoi cette carte existe" icon={CheckCircle2}>
        <ul className="space-y-2 text-sm text-slate-300">
          <li>• Donner un langage commun à la direction, aux ventes, à la programmation et à la cybersécurité.</li>
          <li>• Éviter de comparer un copilote applicatif avec un runtime agentique comme si c’était la même chose.</li>
          <li>• Clarifier les risques : plus l’agent monte en tier, plus la gouvernance, l’identité, les accès et l’audit deviennent critiques.</li>
          <li>• Positionner OpenClaw comme couche Tier 4 : agentic runtime / agent OS, pas simple chatbot.</li>
        </ul>
      </SectionCard>
    </div>
  );
}

function TiersTab() {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <TierCard
        tier="Tier 1"
        title="Agent applicatif"
        icon={AppWindow}
        color="border-blue-300/30"
        definition="L’agent vit dans une seule application. Il comprend très bien le contexte local, mais il sort peu ou pas de son produit."
        examples={[
          'Gmail / Outlook : résumer un fil et proposer une réponse.',
          'HubSpot / Salesforce : qualifier un lead ou expliquer une opportunité.',
          'Notion AI / Google Docs : transformer une note en résumé ou plan.',
          'ERP : expliquer une facture, une commande ou un statut interne.',
        ]}
        boundary="Si l’agent ne contrôle qu’une application et dépend de l’utilisateur pour le reste, c’est Tier 1."
      />
      <TierCard
        tier="Tier 2"
        title="Agent workflow"
        icon={GitBranch}
        color="border-emerald-300/30"
        definition="L’agent traverse plusieurs applications, mais dans un processus défini : ventes, support, facturation, onboarding, RH, etc."
        examples={[
          'Lead entrant → créer contact CRM → envoyer courriel → créer tâche.',
          'Facture PDF → extraction → validation → entrée ERP → approbation.',
          'Ticket support → diagnostic → base de connaissances → réponse client.',
          'Nouvel employé → comptes → checklist → documents → rappel gestionnaire.',
        ]}
        boundary="S’il suit une recette métier relativement stable entre plusieurs systèmes, c’est Tier 2."
      />
      <TierCard
        tier="Tier 3"
        title="Agent opérateur / computer-use"
        icon={Monitor}
        color="border-purple-300/30"
        definition="L’agent peut utiliser un ordinateur comme un humain : navigateur, fichiers, formulaires, applications locales, interfaces sans API."
        examples={[
          'Ouvrir un portail fournisseur, télécharger un rapport et le déposer dans Drive.',
          'Remplir un formulaire web complexe qui n’a pas d’API.',
          'Lire un PDF, copier des données dans Excel, produire un fichier final.',
          'Naviguer dans une vieille application métier et effectuer des clics contrôlés.',
        ]}
        boundary="S’il agit sur l’écran ou le desktop plutôt que seulement par API, c’est Tier 3."
      />
      <TierCard
        tier="Tier 4"
        title="Agent OS / agentic runtime"
        icon={Network}
        color="border-cyan-300/30"
        definition="La couche centrale qui orchestre modèles, outils, skills, mémoire, permissions, sessions, sous-agents et canaux de communication."
        examples={[
          'OpenClaw : messages, outils, skills, mémoire, sous-agents, heartbeats, fichiers, canaux.',
          'Manus-like : environnement agentique complet qui planifie et exécute des travaux complexes.',
          'Devin-like runtime : agent logiciel avec environnement de dev, tâches, tests et PR.',
          'Agent hub interne : route les demandes vers les bons agents, avec audit et contrôle d’accès.',
        ]}
        boundary="S’il coordonne plusieurs agents/outils et devient la couche de contrôle au-dessus des apps, c’est Tier 4."
      />
    </div>
  );
}

function ProduitsTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Exemples de produits par tier" icon={Package}>
        <SimpleTable
          headers={['Tier', 'Produits typiques', 'Ce qu’ils font bien', 'Limite principale']}
          rows={[
            ['T1 — Applicatif', 'Microsoft Copilot dans M365, Salesforce Einstein, HubSpot AI, Notion AI, Intercom Fin, Zendesk AI', 'Augmenter une app précise avec résumé, rédaction, recherche et suggestions contextuelles.', 'Dépend fortement du périmètre de l’application.'],
            ['T2 — Workflow', 'Zapier Agents, Make + LLM, n8n + agents, Lindy, Relevance AI, Gumloop, Bardeen', 'Automatiser un processus multi-apps avec déclencheurs, conditions et validations.', 'Moins fort pour gérer l’ambiguïté hors processus.'],
            ['T3 — Computer-use', 'OpenAI Operator-like, Claude Computer Use, Browser-use, UI agents, RPA enrichi par LLM', 'Utiliser des interfaces humaines quand les APIs n’existent pas ou sont insuffisantes.', 'Plus fragile : UI changeante, latence, erreurs de clics, besoin de supervision.'],
            ['T4 — Agent OS / runtime', 'OpenClaw, Manus-like, Devin-like runtime, AutoGen Studio / LangGraph Platform / CrewAI Enterprise comme couches d’orchestration', 'Orchestrer outils, modèles, mémoire, agents spécialisés, gouvernance et canaux.', 'Exige une gouvernance sérieuse : accès, audit, sécurité, responsabilité.'],
          ]}
        />
      </SectionCard>

      <SectionCard title="Lecture de positionnement" icon={Compass}>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <p className="font-black text-white">Copilot ≠ Agent OS</p>
            <p className="mt-2 text-sm text-slate-300">Un copilote dans une suite bureautique peut être très puissant sans être la couche d’orchestration centrale de l’entreprise.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <p className="font-black text-white">Workflow ≠ Autonomie générale</p>
            <p className="mt-2 text-sm text-slate-300">Un workflow agentique peut être excellent sur une procédure, mais limité dès que le contexte sort de la recette.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <p className="font-black text-white">OpenClaw = Tier 4</p>
            <p className="mt-2 text-sm text-slate-300">OpenClaw agit comme control plane agentique : il reçoit, route, outille, mémorise, délègue et surveille.</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function CasTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Même demande, comportement différent selon le tier" icon={Building2}>
        <SimpleTable
          headers={['Demande', 'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4']}
          rows={[
            [
              'Préparer un suivi client',
              'Rédige un brouillon dans Gmail.',
              'CRM → historique → courriel → tâche de suivi.',
              'Ouvre portail client, télécharge pièces, prépare document.',
              'Choisit le bon agent, consulte mémoire/CRM/Drive, prépare suivi et escalade si risque.',
            ],
            [
              'Traiter une facture',
              'Explique la facture dans l’ERP.',
              'Lit PDF → valide → crée approbation.',
              'Va dans un portail sans API et saisit la facture.',
              'Supervise plusieurs agents finance, journalise, contrôle exceptions et KPI.',
            ],
            [
              'Créer un rapport IA ROI',
              'Résume une page de données.',
              'Extrait DB → calcule KPI → envoie rapport.',
              'Ouvre outils locaux, génère Excel/PDF, dépose fichier.',
              'Planifie récurrence, déclenche skills, envoie email, garde historique et alertes.',
            ],
          ]}
        />
      </SectionCard>

      <SectionCard title="Cas BZ / AIKUB / TactiCE" icon={Network}>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            ['T1', 'Assistant dans PRIsME qui explique une card de gouvernance.'],
            ['T2', 'Agent qui transforme une prise de besoin en tâches CodeHub, brouillon de courriel et résumé direction.'],
            ['T3', 'Agent qui manipule une application client sans API pour extraire des informations et générer un livrable.'],
            ['T4', 'OpenClaw qui reçoit la demande Telegram, choisit la skill, interroge DB/Drive/Gmail, lance un sous-agent et pousse le résultat.'],
          ].map(([tier, text]) => (
            <div key={tier} className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <p className="mb-2 text-sm font-black text-cyan-200">{tier}</p>
              <p className="text-sm leading-relaxed text-slate-300">{text}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function GouvernanceTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Principe PRIsME" icon={ShieldCheck}>
        <p className="text-sm leading-relaxed text-slate-300">
          Plus un agent monte en tier, plus il faut formaliser l’identité, les permissions, les journaux d’audit, les seuils d’autonomie,
          la validation humaine et la responsabilité métier. La classification ne juge pas la qualité du produit : elle juge sa portée et son niveau de risque.
        </p>
      </SectionCard>

      <SectionCard title="Matrice de gouvernance minimale" icon={Layers}>
        <SimpleTable
          headers={['Tier', 'Gouvernance minimale', 'Risque dominant', 'Contrôle recommandé']}
          rows={[
            ['T1', 'Politique d’usage, confidentialité, qualité des réponses.', 'Erreur de contenu ou fuite de contexte local.', 'Validation humaine et périmètre applicatif clair.'],
            ['T2', 'Ownership du workflow, journal des actions, approbations.', 'Action erronée répétée à grande échelle.', 'Human-in-the-loop aux points de décision.'],
            ['T3', 'Session isolée, permissions limitées, enregistrement des actions.', 'Action UI non prévue ou mauvaise interprétation visuelle.', 'Sandbox, confirmation avant action sensible.'],
            ['T4', 'Control plane, RBAC, secrets, audit central, modèle de délégation.', 'Concentration des accès et propagation d’erreurs multi-systèmes.', 'Deny-by-default, moindre privilège, monitoring continu.'],
          ]}
        />
      </SectionCard>

      <SectionCard title="Lien PRIsME / TactiCE" icon={GitBranch}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-cyan-300/20 bg-cyan-400/10 p-4">
            <p className="font-black text-white">PRIsME</p>
            <p className="mt-2 text-sm text-slate-300">Définit la classification, les limites, les standards, les rôles, la maturité et les principes de gouvernance.</p>
          </div>
          <div className="rounded-2xl border border-emerald-300/20 bg-emerald-400/10 p-4">
            <p className="font-black text-white">TactiCE</p>
            <p className="mt-2 text-sm text-slate-300">Exécute les workflows, configure les outils, opère les agents, suit les incidents et mesure les résultats.</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function LimitesTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Ce que cette classification n’est pas" icon={AlertTriangle}>
        <ul className="space-y-2 text-sm text-slate-300">
          <li>• Ce n’est pas une norme officielle ISO, NIST, CSA, OWASP ou gouvernementale.</li>
          <li>• Ce n’est pas une échelle de performance : un excellent Tier 1 peut être plus utile qu’un mauvais Tier 4.</li>
          <li>• Ce n’est pas une certification fournisseur.</li>
          <li>• Ce n’est pas une autorisation automatique de déployer des agents autonomes.</li>
        </ul>
      </SectionCard>

      <SectionCard title="Pourquoi elle reste utile" icon={CheckCircle2}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-sm text-slate-300">
            Elle rend les conversations plus nettes : on sait si on parle d’un chatbot dans une app, d’un workflow, d’un opérateur desktop ou d’un runtime complet.
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-sm text-slate-300">
            Elle permet à BZ d’associer chaque niveau à des exigences de gouvernance proportionnelles au risque.
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Position recommandée BZ" icon={Compass}>
        <p className="text-sm leading-relaxed text-slate-300">
          Utiliser cette grille comme <strong className="text-white">référentiel interne PRIsME en révision</strong>, en attendant qu’un standard de marché plus formel émerge.
          Pour les clients, la présenter comme une classification pratique d’architecture agentique, jamais comme une norme officielle.
        </p>
      </SectionCard>
    </div>
  );
}

export default function TiersLogicielsAgentiquesModal({ isOpen, onClose }: TiersLogicielsAgentiquesModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('synthese');

  const renderTab = () => {
    switch (activeTab) {
      case 'synthese': return <SyntheseTab />;
      case 'tiers': return <TiersTab />;
      case 'produits': return <ProduitsTab />;
      case 'cas': return <CasTab />;
      case 'gouvernance': return <GouvernanceTab />;
      case 'limites': return <LimitesTab />;
      default: return <SyntheseTab />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="max-h-[92vh] w-full max-w-7xl overflow-hidden rounded-3xl border border-cyan-400/20 bg-slate-950 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative overflow-hidden border-b border-white/10 bg-gradient-to-r from-slate-950 via-cyan-950 to-indigo-950 p-6">
              <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
              <button
                onClick={onClose}
                className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="relative pr-12">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-400/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-amber-100">
                  <AlertTriangle className="h-4 w-4" />
                  Classification proposée — pas une norme officielle
                </div>
                <h2 className="text-3xl font-black text-white md:text-4xl">Tiers logiciels agentiques</h2>
                <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-300">
                  Une grille PRIsME pour distinguer agent applicatif, agent workflow, agent opérateur et Agent OS / runtime agentique.
                </p>
              </div>
            </div>

            <div className="border-b border-white/10 bg-slate-900/90 px-4 py-3">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${
                        isActive
                          ? 'bg-cyan-400 text-slate-950 shadow-lg shadow-cyan-400/20'
                          : 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="max-h-[62vh] overflow-y-auto p-6">
              {renderTab()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
